import {ReportLegalConcernFormRequest} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/article_detail_page/v1/service.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  Button,
  COLORS,
  Checkbox,
  FONT_SIZES,
  FONT_WEIGHTS,
  RadioButton,
  RadioGroup,
  Text,
  TextArea,
  TextInput,
  mediaMobileOnly,
  mobileOnlyLinaria,
  tabletAndUpLinaria,
  useShowToast,
} from '@aboutyou/ui-components';
import {emailRegex} from '@aboutyou/utils';
import {styled} from '@linaria/react';
import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useGetReportReasons} from './utils/useGetReportReasons';
import {useSubmitLegalReport} from './utils/useSubmitLegalReport';

export type ReportLegalData = Omit<
  ReportLegalConcernFormRequest,
  'config' | 'productId'
> & {dataAccuracy: boolean};

const ACTIVE_STYLES = {
  backgroundColor: COLORS.Grey4,
  backgroundColorFocused: COLORS.White,
  borderColor: COLORS.Black,
};

const TEXTAREA_LIMIT = {min: 50, max: 500};

export const LegalReportForm: React.FC<{
  productId: number;
  onSuccess: VoidFunction;
}> = ({productId, onSuccess}) => {
  const {t} = useTranslation();
  const showToast = useShowToast();
  const legalReportMutation = useSubmitLegalReport();
  const reportReasons = useGetReportReasons();

  const {
    handleSubmit,
    register,
    formState: {errors},
    watch,
    setValue,
  } = useForm<ReportLegalData>({
    mode: 'onChange',
  });

  useEffect(() => {
    // eslint-disable-next-line local-rules/set-state-in-use-effect
    // remove extra space
    setValue('description', watch('description').replace(/\s{2,}/g, ' '));
  }, [watch('description')]);

  const handleSubmitLegalReport = async (data: ReportLegalData) => {
    try {
      await legalReportMutation({
        reason: data.reason,
        productId,
        description: data.description,
        firstName: data.firstName ?? null,
        lastName: data.lastName ?? null,
        email: data.email ?? null,
        isTrustedFlagger: data.isTrustedFlagger,
      });
      onSuccess();
    } catch (e) {
      showToast({
        content: t('Oops, there is a technical issue. Please try again later.'),
        type: 'ERROR',
      });
    }
  };

  return (
    <Form
      data-testid="legalReportForm"
      onSubmit={handleSubmit(handleSubmitLegalReport)}
      method="POST"
    >
      {/* Report reason */}
      <Section>
        <SectionTitleWrapper>
          <SectionTitle>
            {t('Please select the reason for your report *')}
          </SectionTitle>
          {errors.reason && (
            <SectionSubtitle $hasError>
              {t('Please select a reason.')}
            </SectionSubtitle>
          )}
        </SectionTitleWrapper>
        <ReportReason>
          {reportReasons.map(({label, value}) => (
            <StyledRadioButton
              key={value}
              boxedMode
              label={label}
              value={value}
              checked={`${watch('reason')}` === `${value}`}
              {...register('reason', {
                required: true,
              })}
            />
          ))}
        </ReportReason>
      </Section>
      {/* Tell us more */}
      <Section>
        <SectionTitleWrapper>
          <SectionTitle>{t('Tell us more about the issue *')}</SectionTitle>
          <SectionSubtitle $hasError={Boolean(errors.description)}>
            {t('Please input a minimum of {limit} characters.', {
              limit: 50,
            })}
          </SectionSubtitle>
        </SectionTitleWrapper>
        <TextArea
          activeStyle={ACTIVE_STYLES}
          placeholder={t('Describe your concern...')}
          height={180}
          maxLength={TEXTAREA_LIMIT.max}
          errorMessage={errors.description?.message}
          {...register('description', {
            required: t(
              'Add your description. Use at least {limit} characters.',
              {limit: TEXTAREA_LIMIT.min},
            ),
            minLength: {
              value: TEXTAREA_LIMIT.min,
              message: t(
                'Add your description. Use at least {limit} characters.',
                {limit: TEXTAREA_LIMIT.min},
              ),
            },
            pattern: {value: /^[^<>]*$/, message: t('Invalid format')},
          })}
        />
      </Section>
      {/* Contact details */}
      <Section $hideSeparator>
        <SectionTitleWrapper>
          <SectionTitle>{t('Contact Details')}</SectionTitle>
          <SectionSubtitle>
            {t(
              'Please note that we need your contact details if you wish to be informed about the proceedings of your report.',
            )}
          </SectionSubtitle>
        </SectionTitleWrapper>
        <ContactDetails>
          <TextInput
            activeStyle={ACTIVE_STYLES}
            label={t('First name')}
            {...register('firstName')}
          />
          <TextInput
            activeStyle={ACTIVE_STYLES}
            label={t('Last name')}
            {...register('lastName')}
          />
          <TextInput
            activeStyle={ACTIVE_STYLES}
            errorMessage={errors.email?.message}
            label={t('Email address')}
            {...register('email', {
              pattern: {
                value: emailRegex,
                message: t('Invalid format'),
              },
            })}
          />
          {/* Trusted flagger */}
          <ContactDetailsSection>
            <StyledCheckbox
              label={t(
                "Yes, I'm a trusted flagger under Article 22 of the Digital Services Act.",
              )}
              subLabel={
                <TrustedFlaggerSublabel
                  security="px12"
                  weight="mediumBold"
                  color="Grey0"
                  style={{marginLeft: '34px'}}
                >
                  {t(
                    'If you are a trusted flagger under Article 22 of the Digital Services Act, please use the email address registered with the authorities. This allows us to process your notice accurately.',
                  )}
                </TrustedFlaggerSublabel>
              }
              {...register('isTrustedFlagger')}
            />
          </ContactDetailsSection>
          {/* Data Accuracy */}
          <ContactDetailsSection>
            {errors.dataAccuracy && (
              <SectionTitleWrapper>
                <SectionSubtitle $hasError>
                  {t('Please check the box below in order to submit the form.')}
                </SectionSubtitle>
              </SectionTitleWrapper>
            )}
            <StyledCheckbox
              dataTestId="legalReportDataAccuracy"
              label={t(
                "I confirm that I'm acting in good faith and my report is as accurate and complete as I could make it. *",
              )}
              {...register('dataAccuracy', {required: true})}
            />
          </ContactDetailsSection>
        </ContactDetails>
      </Section>
      <SubmitButton htmlType="submit">{t('Submit legal report')}</SubmitButton>
    </Form>
  );
};

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 64px;

  ${tabletAndUpLinaria(`
    padding: 32px;
    border-radius: 8px;
    border: 1px solid ${COLORS.Grey3};
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.04), 0px 10px 15px 0px rgba(0, 0, 0, 0.04);
  `)}
`;

const Section = styled.div<{$hideSeparator?: boolean}>`
  display: flex;
  flex-direction: column;

  @media ${mediaMobileOnly} {
    position: relative;

    &::after {
      display: ${props => (props.$hideSeparator ? 'none' : 'block')};
      position: absolute;
      left: -16px;
      bottom: -36px;
      content: '';
      width: calc(100% + 32px);
      height: 4px;
      background: ${COLORS.Grey4};
    }
  }
`;

const SectionTitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 16px;
  gap: 4px;
`;

const SectionTitle = styled(Text)`
  font-size: ${FONT_SIZES.px16};
  font-weight: ${FONT_WEIGHTS.mediumHeavy};
  line-height: 1.3;
`;

const SectionSubtitle = styled(Text)<{$hasError?: boolean}>`
  color: ${props =>
    props.$hasError ? COLORS.NotificationsRed100 : COLORS.Grey0};
  font-weight: ${FONT_WEIGHTS.mediumBold};
  line-height: 1.45;
`;

const ReportReason = styled(RadioGroup)`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledRadioButton = styled(RadioButton)`
  width: 100%;
  padding: 16px;
  border-radius: 4px;

  > div {
    align-items: center;
  }

  span {
    font-weight: ${FONT_WEIGHTS.mediumBold};
    color: ${COLORS.Black};
  }
`;

const SubmitButton = styled(Button)`
  ${mobileOnlyLinaria(`margin-top: -32px;`)}
`;

const ContactDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContactDetailsSection = styled.div`
  margin-top: 16px;
`;

const StyledCheckbox = styled(Checkbox)`
  &&& {
    color: ${COLORS.Black};
  }
`;

const TrustedFlaggerSublabel = styled(Text)`
  margin: 4px 0 0 34px;
  line-height: 1.3;
`;
